import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../api/axiosInstance'
import Swal from 'sweetalert2'

const COLORS = { gray: '#5B6B79', blue: '#96C5F7', green: '#2CA87F', red: '#9E2B25', orange: '#F29E4C' }

const thStyle = {
  fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 14px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
}
const tdStyle = {
  fontSize: 13, color: '#2d3748', padding: '9px 14px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
}

const Toggle = ({ value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{
      width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative',
      background: value ? COLORS.green : '#dde3eb', transition: 'background 0.2s', flexShrink: 0,
    }}
  >
    <div style={{
      position: 'absolute', top: 3, left: value ? 23 : 3,
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 4px rgba(0,0,0,0.18)', transition: 'left 0.2s',
    }} />
  </div>
)

export default function Postcare() {
  const navigate = useNavigate()

  // ── Settings ──────────────────────────────────────────────────────────────
  const [settingsOpen, setSettingsOpen]       = useState(false)
  const [enabled, setEnabled]                 = useState(false)
  const [delayHours, setDelayHours]           = useState(24)
  const [saving, setSaving]                   = useState(false)
  const [loadingSettings, setLoadingSettings] = useState(false)

  // ── Triggers ──────────────────────────────────────────────────────────────
  const [triggers, setTriggers]           = useState([])
  const [loadingTriggers, setLoadingTrig] = useState(false)
  const [triggerModalOpen, setTrigModal]  = useState(false)
  const [services, setServices]           = useState([])
  const [emailTemplates, setEmailTemplates] = useState([])
  const [selService, setSelService]       = useState('')
  const [selTemplate, setSelTemplate]     = useState('')
  const [addingTrigger, setAddingTrigger] = useState(false)
  const [loadingOptions, setLoadingOpts]  = useState(false)

  // ── Logs ──────────────────────────────────────────────────────────────────
  const [logs, setLogs]               = useState([])
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [logsTotal, setLogsTotal]     = useState(0)
  const [logsPage, setLogsPage]       = useState(1)
  const [statusFilter, setStatusFilter] = useState('all')
  const LOGS_PER_PAGE = 10

  // ── Load settings on mount ────────────────────────────────────────────────
  useEffect(() => {
    loadSettings()
    loadTriggers()
    loadLogs(1)
  }, [])

  const loadSettings = async () => {
    setLoadingSettings(true)
    try {
      const [modRes, settingsRes] = await Promise.all([
        axiosInstance.get('getModuleSettings'),
        axiosInstance.get('getPostcareSettings'),
      ])
      if (modRes.data?.success) {
        const row = modRes.data.data.find(r => r.module_name === 'postcare')
        setEnabled(Boolean(row?.enable))
      }
      if (settingsRes.data?.success) {
        setDelayHours(settingsRes.data.data.delay_hours ?? 24)
      }
    } catch (e) {
      console.error('Failed to load postcare settings', e)
    } finally {
      setLoadingSettings(false)
    }
  }

  const handleSave = async () => {
    const hours = parseInt(delayHours)
    if (isNaN(hours) || hours < 0) {
      Swal.fire('Validation', 'Delay Time must be 0 or greater.', 'warning'); return
    }
    setSaving(true)
    try {
      await Promise.all([
        axiosInstance.put('updateModuleSetting', { module_name: 'postcare', enable: enabled ? 1 : 0 }),
        axiosInstance.put('updatePostcareSettings', { delay_hours: hours }),
      ])
      Swal.fire('Saved', 'Postcare settings saved successfully.', 'success')
      setSettingsOpen(false)
    } catch (e) {
      Swal.fire('Error', e.response?.data?.message || 'Failed to save settings.', 'error')
    } finally {
      setSaving(false)
    }
  }

  // ── Triggers ──────────────────────────────────────────────────────────────
  const loadTriggers = async () => {
    setLoadingTrig(true)
    try {
      const res = await axiosInstance.get('getPostcareTriggers')
      setTriggers(res.data?.data || [])
    } catch { setTriggers([]) }
    finally { setLoadingTrig(false) }
  }

  const loadLogs = async (page = logsPage) => {
    setLoadingLogs(true)
    try {
      const offset = (page - 1) * LOGS_PER_PAGE
      const res = await axiosInstance.get(`getPostcareEmailLogs?limit=${LOGS_PER_PAGE}&offset=${offset}`)
      setLogs(res.data?.data || [])
      setLogsTotal(res.data?.total || 0)
      setLogsPage(page)
    } catch { setLogs([]) }
    finally { setLoadingLogs(false) }
  }

  const openTriggerModal = async () => {
    setSelService(''); setSelTemplate('')
    setTrigModal(true)
    setLoadingOpts(true)
    try {
      const [svcRes, tplRes] = await Promise.all([
        axiosInstance.get('getServices'),
        axiosInstance.get('getAllTemplates'),
      ])
      setServices(svcRes.data?.data || [])
      setEmailTemplates(Array.isArray(tplRes.data?.data) ? tplRes.data.data : [])
    } catch { setServices([]); setEmailTemplates([]) }
    finally { setLoadingOpts(false) }
  }

  const handleAddTrigger = async () => {
    if (!selService || !selTemplate) {
      Swal.fire('Validation', 'Please select both a service and an email template.', 'warning'); return
    }
    setAddingTrigger(true)
    try {
      await axiosInstance.post('createPostcareTrigger', {
        service_id: parseInt(selService),
        email_template_id: parseInt(selTemplate),
      })
      setTrigModal(false)
      loadTriggers()
    } catch (e) {
      Swal.fire('Error', e.response?.data?.message || 'Failed to create trigger.', 'error')
    } finally {
      setAddingTrigger(false)
    }
  }

  const handleDeleteTrigger = async (id) => {
    const confirm = await Swal.fire({
      title: 'Delete Trigger?', text: 'This trigger will be removed.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Yes, delete', confirmButtonColor: COLORS.red,
    })
    if (!confirm.isConfirmed) return
    try {
      await axiosInstance.delete(`deletePostcareTrigger/${id}`)
      loadTriggers()
    } catch (e) {
      Swal.fire('Error', e.response?.data?.message || 'Failed to delete trigger.', 'error')
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => navigate('/Admin/Medicals')}
              style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 15, marginRight: 4 }}
            >
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-alert-circle" style={{ color: COLORS.blue, fontSize: 19 }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a202c' }}>Postcare</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Manage postcare triggers and settings</div>
            </div>
            <button
              onClick={() => setSettingsOpen(true)}
              style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid #e8ecf0', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '18'; e.currentTarget.style.color = COLORS.blue; e.currentTarget.style.borderColor = COLORS.blue }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray; e.currentTarget.style.borderColor = '#e8ecf0' }}
            >
              <i className="ti ti-settings" style={{ fontSize: 16 }} /> Settings
            </button>
          </div>

          {/* Triggers section */}
          <div style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Triggers</div>
                <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 2 }}>
                  Link a service to an email template — the template is sent to the patient after the delay period.
                </div>
              </div>
              <button
                onClick={openTriggerModal}
                className="bgBtn btn-sm d-flex align-items-center gap-1"
                style={{ fontSize: 13, padding: '7px 16px' }}
              >
                <i className="ti ti-plus" style={{ fontSize: 15 }} /> Add Trigger
              </button>
            </div>

            <div style={{ borderRadius: 10, border: '1px solid #e8ecf0', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, borderLeft: `3px solid ${COLORS.blue}` }}>#</th>
                    <th style={thStyle}>Service</th>
                    <th style={thStyle}>Email Template</th>
                    <th style={thStyle}>Subject</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTriggers ? (
                    <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: COLORS.gray, padding: '32px 0' }}>Loading...</td></tr>
                  ) : triggers.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: COLORS.gray, padding: '40px 0' }}>
                        <i className="ti ti-link-off" style={{ fontSize: 30, display: 'block', marginBottom: 8, opacity: 0.35 }} />
                        No triggers yet — click <strong>Add Trigger</strong> to create one.
                      </td>
                    </tr>
                  ) : triggers.map((t, i) => (
                    <tr key={t.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{ ...tdStyle, borderLeft: `3px solid ${COLORS.blue}` }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.blue, background: `${COLORS.blue}15`, padding: '2px 8px', borderRadius: 6 }}>{t.id}</span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <i className="ti ti-clipboard-list" style={{ color: COLORS.green, fontSize: 15 }} />
                          <span style={{ fontWeight: 600 }}>{t.service_name || '—'}</span>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <i className="ti ti-mail" style={{ color: COLORS.blue, fontSize: 15 }} />
                          <span>{t.template_name || '—'}</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: COLORS.gray, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.subject || '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button
                          onClick={() => handleDeleteTrigger(t.id)}
                          title="Delete trigger"
                          style={{ width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer', background: COLORS.red + '18', color: COLORS.red, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}
                          onMouseEnter={e => e.currentTarget.style.background = COLORS.red + '35'}
                          onMouseLeave={e => e.currentTarget.style.background = COLORS.red + '18'}
                        >
                          <i className="ti ti-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Email Log section */}
          <div style={{ padding: '0 24px 24px' }}>
            <div style={{ borderTop: '1px solid #e8ecf0', paddingTop: 20, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Email Log</div>
                <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 2 }}>History of all postcare emails queued and sent.</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Status filter */}
                <select
                  value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); loadLogs(1) }}
                  style={{ height: 32, borderRadius: 7, border: '1px solid #dde3eb', padding: '0 10px', fontSize: 12, color: COLORS.gray, background: '#fff', cursor: 'pointer' }}
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                </select>
                <button
                  onClick={() => loadLogs(1)}
                  title="Refresh logs"
                  style={{ width: 32, height: 32, borderRadius: 7, border: '1px solid #dde3eb', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}
                >
                  <i className="ti ti-refresh" />
                </button>
              </div>
            </div>

            <div style={{ borderRadius: 10, border: '1px solid #e8ecf0', overflow: 'hidden' }}>
              {/* Sticky header + fixed-height scrollable body */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
                  <thead>
                    <tr>
                      {['Patient', 'Service', 'Scheduled', 'Sent At', 'Status', 'Error'].map((h, i) => (
                        <th key={h} style={{ ...thStyle, padding: '6px 10px', position: 'sticky', top: 0, zIndex: 1, borderLeft: i === 0 ? `3px solid ${COLORS.blue}` : undefined }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>

              {/* Scrollable body — max 320 px, ~8 rows */}
              <div style={{ maxHeight: 320, overflowY: 'auto', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
                  <tbody>
                    {loadingLogs ? (
                      <tr><td colSpan={6} style={{ padding: '24px 0', textAlign: 'center', color: COLORS.gray, fontSize: 12 }}>Loading...</td></tr>
                    ) : logs.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ padding: '28px 0', textAlign: 'center', color: COLORS.gray, fontSize: 12 }}>
                          <i className="ti ti-mail-off" style={{ fontSize: 22, display: 'block', marginBottom: 6, opacity: 0.35 }} />
                          No email logs yet.
                        </td>
                      </tr>
                    ) : logs
                      .filter(l => statusFilter === 'all' || l.status === statusFilter)
                      .map((log, i) => {
                        const statusColor = log.status === 'sent' ? COLORS.green : log.status === 'failed' ? COLORS.red : COLORS.orange
                        const statusIcon  = log.status === 'sent' ? 'ti-circle-check' : log.status === 'failed' ? 'ti-circle-x' : 'ti-clock'
                        const fmt = (dt) => dt ? new Date(dt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
                        const compact = { fontSize: 11, color: '#2d3748', padding: '6px 10px', verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }
                        return (
                          <tr key={log.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ ...compact, borderLeft: `3px solid ${COLORS.blue}` }}>
                              <div style={{ fontWeight: 600 }}>{log.patient_name || '—'}</div>
                              <div style={{ fontSize: 10, color: COLORS.gray }}>{log.patient_email}</div>
                            </td>
                            <td style={compact}>{log.service_name || '—'}</td>
                            <td style={{ ...compact, color: COLORS.gray }}>{fmt(log.scheduled_at)}</td>
                            <td style={{ ...compact, color: COLORS.gray }}>{fmt(log.sent_at)}</td>
                            <td style={compact}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: statusColor + '18', color: statusColor, textTransform: 'capitalize' }}>
                                <i className={`ti ${statusIcon}`} style={{ fontSize: 11 }} /> {log.status}
                              </span>
                            </td>
                            <td style={{ ...compact, color: COLORS.red, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }} title={log.error_message || ''}>
                              {log.error_message || '—'}
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>

              {/* Footer: count + pagination inside the card border */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderTop: '1px solid #e8ecf0', background: '#fafbfc' }}>
                <span style={{ fontSize: 11, color: COLORS.gray }}>{logsTotal} total record{logsTotal !== 1 ? 's' : ''}</span>
                {logsTotal > LOGS_PER_PAGE && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <button onClick={() => loadLogs(logsPage - 1)} disabled={logsPage === 1}
                      style={{ width: 24, height: 24, borderRadius: 5, border: `1px solid ${COLORS.blue}`, background: logsPage === 1 ? '#f5f7fa' : COLORS.blue, color: logsPage === 1 ? '#aaa' : '#fff', cursor: logsPage === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                      <i className="ti ti-chevron-left" />
                    </button>
                    <span style={{ fontSize: 11, color: COLORS.gray }}>Page {logsPage} / {Math.ceil(logsTotal / LOGS_PER_PAGE)}</span>
                    <button onClick={() => loadLogs(logsPage + 1)} disabled={logsPage >= Math.ceil(logsTotal / LOGS_PER_PAGE)}
                      style={{ width: 24, height: 24, borderRadius: 5, border: `1px solid ${COLORS.blue}`, background: logsPage >= Math.ceil(logsTotal / LOGS_PER_PAGE) ? '#f5f7fa' : COLORS.blue, color: logsPage >= Math.ceil(logsTotal / LOGS_PER_PAGE) ? '#aaa' : '#fff', cursor: logsPage >= Math.ceil(logsTotal / LOGS_PER_PAGE) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                      <i className="ti ti-chevron-right" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Settings Modal ─────────────────────────────────────────────────── */}
      {settingsOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setSettingsOpen(false)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 400, boxShadow: '0 8px 40px rgba(0,0,0,0.18)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-settings" style={{ color: COLORS.blue, fontSize: 17 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Postcare Settings</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>Configure postcare behaviour</div>
              </div>
              <button onClick={() => setSettingsOpen(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
              {loadingSettings ? (
                <div style={{ textAlign: 'center', color: COLORS.gray, fontSize: 13, padding: '20px 0' }}>Loading...</div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 10, border: '1px solid #e8ecf0', background: '#fafbfc' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>Enable Postcare</div>
                      <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 2 }}>Activate the postcare feature system-wide</div>
                    </div>
                    <Toggle value={enabled} onChange={setEnabled} />
                  </div>

                  <div style={{ padding: '14px 16px', borderRadius: 10, border: '1px solid #e8ecf0', background: '#fafbfc' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c', marginBottom: 4 }}>Delay Time</div>
                    <div style={{ fontSize: 11, color: COLORS.gray, marginBottom: 12 }}>Hours after service completion before postcare is sent. Set to <strong>0</strong> to send instantly.</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input type="number" min="0" value={delayHours} onChange={e => setDelayHours(e.target.value)}
                        style={{ width: 90, height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 14, fontWeight: 600, color: '#1a202c', outline: 'none', textAlign: 'center', background: '#fff' }} />
                      <span style={{ fontSize: 13, color: COLORS.gray }}>
                        {delayHours == 0 ? 'hours (instant)' : `hour${delayHours != 1 ? 's' : ''}`}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={{ padding: '14px 20px', borderTop: '1px solid #e8ecf0', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setSettingsOpen(false)} style={{ padding: '7px 18px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving || loadingSettings} className="bgBtn btn-sm" style={{ padding: '7px 20px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                {saving ? <><i className="ti ti-loader-2 ti-spin" /> Saving...</> : <><i className="ti ti-device-floppy" /> Save Settings</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Trigger Modal ──────────────────────────────────────────────── */}
      {triggerModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setTrigModal(false)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 440, boxShadow: '0 8px 40px rgba(0,0,0,0.18)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-link" style={{ color: COLORS.blue, fontSize: 17 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Add Trigger</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>Link a service to an email template</div>
              </div>
              <button onClick={() => setTrigModal(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            {/* Modal body */}
            <div style={{ padding: '22px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {loadingOptions ? (
                <div style={{ textAlign: 'center', color: COLORS.gray, fontSize: 13, padding: '20px 0' }}>Loading options...</div>
              ) : (
                <>
                  {/* Service dropdown */}
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                      Service
                    </label>
                    <select
                      value={selService}
                      onChange={e => setSelService(e.target.value)}
                      style={{ width: '100%', height: 38, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#2d3748', background: '#fff', outline: 'none', cursor: 'pointer' }}
                    >
                      <option value="">— Select a service —</option>
                      {services.map(s => (
                        <option key={s.id} value={s.id}>{s.name} {s.code ? `(${s.code})` : ''}</option>
                      ))}
                    </select>
                  </div>

                  {/* Arrow indicator */}
                  <div style={{ textAlign: 'center', color: COLORS.blue, fontSize: 20 }}>
                    <i className="ti ti-arrow-down" />
                  </div>

                  {/* Email template dropdown */}
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                      Email Template
                    </label>
                    <select
                      value={selTemplate}
                      onChange={e => setSelTemplate(e.target.value)}
                      style={{ width: '100%', height: 38, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#2d3748', background: '#fff', outline: 'none', cursor: 'pointer' }}
                    >
                      <option value="">— Select an email template —</option>
                      {emailTemplates.map(t => (
                        <option key={t.id} value={t.id}>{t.template_name} — {t.subject}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            {/* Modal footer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid #e8ecf0', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setTrigModal(false)} style={{ padding: '7px 18px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddTrigger} disabled={addingTrigger || loadingOptions} className="bgBtn btn-sm" style={{ padding: '7px 20px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                {addingTrigger ? <><i className="ti ti-loader-2 ti-spin" /> Saving...</> : <><i className="ti ti-link" /> Add Trigger</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
